import { collection, getDocs, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { usableTimeZone } from "./agenda-schedule";
import { APP_LOCALE } from "utils";

export interface PresentingSession {
  id: string;
  title: string;
  startsAt: Date | null;
}

export type PresentingSessionsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; sessions: PresentingSession[] };

/**
 * The sessions this person is down to present, read when they ask to withdraw.
 *
 * Withdrawing takes their id off `presenters` on every one of these, so they
 * are told before they confirm. Loaded on demand rather than with the page:
 * `enabled` is the dialog being open, so somebody who never withdraws pays
 * nothing for this.
 *
 * `presenterIds array-contains` needs no composite index (there is no orderBy
 * on the query) and no rules change: `agenda_items` is already readable by any
 * member, which the person still is at the moment they are asked.
 *
 * Only attendee-id slots are found, which is exactly the set that changes.
 * A `speakers` row naming the same human is the organiser's own content and
 * survives the withdrawal.
 */
export function usePresentingSessions(
  eventId: string,
  attendeeId: string | undefined,
  enabled: boolean,
): PresentingSessionsState {
  const [state, setState] = useState<PresentingSessionsState>({
    status: "idle",
  });

  useEffect(() => {
    if (!enabled || !attendeeId) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });

    getDocs(
      query(
        collection(db, "events", eventId, "agenda_items"),
        where("presenterIds", "array-contains", attendeeId),
      ),
    )
      .then((snap) => {
        if (cancelled) return;
        const sessions: PresentingSession[] = [];
        snap.forEach((item) => {
          const data = item.data() as Record<string, unknown>;
          sessions.push({
            id: item.id,
            title: typeof data.title === "string" ? data.title : "Session",
            startsAt: toDate(data.startsAt),
          });
        });
        sessions.sort(byStart);
        setState({ status: "ready", sessions });
      })
      .catch(() => {
        if (cancelled) return;
        // A failed lookup must not trap somebody in an event they want to
        // leave, so an empty answer stands in for an unknown one and the
        // plain confirmation is shown. The withdrawal itself is unaffected:
        // the trigger takes them off their sessions either way.
        setState({ status: "ready", sessions: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, attendeeId, enabled]);

  return state;
}

/** "Thu 10 Sep, 09:00" on the event's clock, the one the agenda shows. */
export function formatSessionStart(
  startsAt: Date | null,
  timeZone?: string,
): string {
  if (!startsAt) return "";
  return startsAt.toLocaleString(APP_LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: usableTimeZone(timeZone),
  });
}

function toDate(value: unknown): Date | null {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

function byStart(a: PresentingSession, b: PresentingSession): number {
  if (!a.startsAt) return b.startsAt ? 1 : 0;
  if (!b.startsAt) return -1;
  return a.startsAt.getTime() - b.startsAt.getTime();
}
